import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getFlujoCajaComparativa, getEjercicios, getEmpresa, exportarFlujoVariaciones, getProyeccionResultado } from "@/lib/api";
import { formatCurrency, downloadBlob, parseBlobError } from "@/lib/utils";
import { TABLA_ANIOS, ColsAnios, COL_ANIO_MONO } from "@/components/ui/tabla-anios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, 
  ArrowRightLeft, 
  ChevronDown, 
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Info,
  TrendingUp,
  Building2,
  Wallet,
  Wrench,
  FileSpreadsheet
} from "lucide-react";

export default function ComparativaFlujosPage() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);
  const [selectedAnios, setSelectedAnios] = useState([]);
  const [flujosCaja, setFlujosCaja] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFlujos, setLoadingFlujos] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    cfo: true,
    cfi: true,
    cff: true,
    total: true,
    conciliacion: true
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [exportandoVariaciones, setExportandoVariaciones] = useState(false);
  // Histórico + proyectado en todas partes (regla 30-09): los años del
  // consolidado entran como columnas OPCIONALES marcadas "Proy." en amarillo.
  const [proy, setProy] = useState(null);
  const [selectedProyAnios, setSelectedProyAnios] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, [empresaId]);

  useEffect(() => {
    if (selectedAnios.length >= 1) {
      loadFlujosCaja();
    } else {
      setFlujosCaja([]);
    }
  }, [selectedAnios]);

  const loadInitialData = async () => {
    try {
      const [empresaRes, ejerciciosRes] = await Promise.all([
        getEmpresa(empresaId),
        getEjercicios(empresaId),
      ]);
      setEmpresa(empresaRes.data);
      const ejerciciosConDatos = ejerciciosRes.data
        .filter((e) => e.total_cuentas > 0)
        .sort((a, b) => a.anio - b.anio);
      setEjercicios(ejerciciosConDatos);
      
      // Auto-seleccionar todos los años (excepto el primero que no tiene anterior)
      const anios = ejerciciosConDatos.map((e) => e.anio);
      if (anios.length > 1) {
        setSelectedAnios(anios.slice(1)); // Excluir el primer año
      }
      // Proyección: carga tolerante (empresa sin config ⇒ solo histórico)
      try {
        const proyRes = await getProyeccionResultado(empresaId);
        setProy(proyRes.data);
        setSelectedProyAnios(proyRes.data?.anios_proyectados || []);
      } catch {
        setProy(null);
        setSelectedProyAnios([]);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
      navigate("/empresas");
    } finally {
      setLoading(false);
    }
  };

  const loadFlujosCaja = async () => {
    setLoadingFlujos(true);
    try {
      const response = await getFlujoCajaComparativa(empresaId, selectedAnios.join(","));
      setFlujosCaja(response.data);
    } catch (error) {
      toast.error("Error al cargar flujos de caja");
    } finally {
      setLoadingFlujos(false);
    }
  };

  const toggleAnio = (anio) => {
    // No permitir seleccionar el primer año (no tiene año anterior)
    const primerAnio = ejercicios[0]?.anio;
    if (anio === primerAnio) {
      toast.info(`El año ${anio} no puede incluirse (no hay datos del año anterior)`);
      return;
    }
    
    setSelectedAnios((prev) =>
      prev.includes(anio) ? prev.filter((a) => a !== anio) : [...prev, anio].sort()
    );
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleItemClick = (item, anio) => {
    const flujo = flujosCaja.find(f => f.anio === anio);
    if (!flujo || flujo.error) return;
    
    // Navegar por la estructura del flujo para encontrar el item
    let data = null;
    const parts = item.split('.');
    if (parts[0] === 'cfo') data = flujo.cfo?.[parts[1]];
    else if (parts[0] === 'cfi') data = flujo.cfi?.[parts[1]];
    else if (parts[0] === 'cff') data = flujo.cff?.[parts[1]];
    
    if (data) {
      setSelectedItem({ ...data, label: getItemLabel(item), anio });
    }
  };

  const handleExportarVariaciones = async (anio) => {
    if (exportandoVariaciones) return;
    const flujo = flujosCaja.find((f) => f.anio === anio);
    if (!flujo?.ejercicio_id) return;
    setExportandoVariaciones(true);
    try {
      const response = await exportarFlujoVariaciones(flujo.ejercicio_id);
      downloadBlob(response.data, `FlujoVariaciones_${anio || ""}.xlsx`);
      toast.success("Variaciones exportadas correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar las variaciones"));
    } finally {
      setExportandoVariaciones(false);
    }
  };

  const getItemLabel = (item) => {
    const labels = {
      'cfo.resultado': 'Resultado del ejercicio',
      'cfo.amortizacion': 'Amortización (sin caja)',
      'cfo.impuesto_diferido': 'Impuesto diferido (474/479)',
      'cfo.existencias': 'Existencias',
      'cfo.clientes': 'Clientes',
      'cfo.proveedores': 'Proveedores',
      'cfo.periodificaciones_activo': 'Periodificaciones de activo',
      'cfo.periodificaciones_pasivo': 'Periodificaciones de pasivo',
      'cfo.provisiones': 'Provisiones',
      'cfi.inmovilizado': 'Inmovilizado',
      'cfi.inv_inmobiliarias': 'Inversiones inmobiliarias',
      'cfi.empresas_grupo': 'Empresas del grupo',
      'cfi.inv_financieras': 'Inversiones financieras',
      'cfi.otros': 'Otros del modelo',
      'cff.patrimonio': 'Patrimonio neto',
      'cff.deuda': 'Deuda',
      'cff.deuda_grupo': 'Deudas con empresas del grupo'
    };
    return labels[item] || item;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const primerAnio = ejercicios[0]?.anio;

  // ── Años PROYECTADOS como flujos sintéticos con el MISMO shape ───────────
  // Fuente única: flujo_proyectado del consolidado (cfo/cfi/cff/Δ tesorería/
  // tesorería final). Solo los TOTALES por bloque existen en proyección; las
  // sub-filas muestran "—" (el desglose por subcuenta no existe a futuro).
  // La conciliación se DERIVA del propio consolidado (inicial = final − Δ),
  // que ya garantiza Δ = CFO+CFI+CFF y tesorería final = balance (candados
  // del backend): nada se recalcula ni se aproxima aquí.
  const aniosProyTodos = proy?.anios_proyectados || [];
  const aniosProySel = aniosProyTodos
    .filter((a) => selectedProyAnios.includes(a))
    .sort((a, b) => a - b);
  // E6 (Pau oct-2026): detalle por partidas TAMBIÉN en los años proyectados,
  // derivado del consolidado (mismas series que el balance proyectado, al
  // euro): resultado + amortización del pyg_completo, Δ de las series de
  // circulante, CAPEX como inversión y Δ deuda/PN como financiación. Lo que
  // el modelo enruta aparte (intereses capitalizados, eventos de balance) cae
  // en una fila "Otros del modelo" para que cada bloque SUME su total.
  const _filaC = (concepto, a) =>
    (proy?.pyg_completo || []).find((x) => x.concepto === concepto)?.por_anio?.[String(a)];
  const _balS = (concepto, a) =>
    (proy?.balance?.partidas || []).find((p) => p.concepto === concepto && !p.sub)
      ?.por_anio?.[String(a)];
  const _aniosSerie = [...(proy?.anios_historicos || []), ...(proy?.anios_proyectados || [])];
  const _prevAnio = (a) => {
    const i = _aniosSerie.indexOf(a);
    return i > 0 ? _aniosSerie[i - 1] : null;
  };
  const _delta = (concepto, a) => {
    const prev = _prevAnio(a);
    const v = _balS(concepto, a);
    const v0 = prev != null ? _balS(concepto, prev) : null;
    return v != null && v0 != null ? v - v0 : null;
  };
  const flujosProy = aniosProySel.map((a) => {
    const f = (proy?.flujo_proyectado || []).find((x) => x.anio === a);
    if (!f) return { anio: a, proyectado: true, error: true };
    const neto = _filaC("Resultado neto", a) ?? null;
    const amort = _filaC("Amortización", a) ?? null;
    const dEx = _delta("Existencias", a);
    const dCli = _delta("Clientes", a);
    const dProv = _delta("Proveedores", a);
    const dDeuda = (() => {
      const lp = _delta("Deuda financiera LP", a);
      const cp = _delta("Deuda financiera CP", a);
      return lp != null && cp != null ? lp + cp : null;
    })();
    const dPN = _delta("Patrimonio neto", a);
    const cfoRows = {
      resultado: neto,
      amortizacion: amort,
      impuesto_diferido: 0,
      existencias: dEx != null ? -dEx : null,
      clientes: dCli != null ? -dCli : null,
      proveedores: dProv,
      periodificaciones_activo: 0,
      periodificaciones_pasivo: 0,
      provisiones: 0,
    };
    const _sum = (o) => Object.values(o).reduce((s, v) => s + (v ?? 0), 0);
    const cfoOtros = f.cfo != null ? f.cfo - _sum(cfoRows) : null;
    // Revisión externa oct-2026 (#6a): antes TODO el CFI caía en la fila
    // "Inmovilizado" (una participación comprada con "Otras hipótesis" salía
    // como inmovilizado). Ahora cada fila se deriva del consolidado:
    //   Inmovilizado = −(Δ Inmovilizado neto + amortización) = CAPEX bruto
    //     (incluye los eventos de las líneas I/II/III, que SÍ son inmovilizado);
    //   Empresas del grupo / Inversiones financieras = −Δ de los eventos
    //     declarados sobre SUS líneas (eventos_por_linea, acumulado);
    //   el resto del modelo cae en "Otros" para que el bloque sume su total.
    const _evAcum = (lineas, a2) => lineas.reduce(
      (s, p) => s + (proy?.eventos_por_linea?.[p]?.por_anio?.[String(a2)] ?? 0), 0);
    const _evDelta = (lineas) => {
      const prev = _prevAnio(a);
      return _evAcum(lineas, a) - (prev != null ? _evAcum(lineas, prev) : 0);
    };
    const L_EV_GRUPO = ["IV. Inversiones en empresas del grupo y asociadas a l/p",
                        "III. Inversiones en empresas del grupo y asociadas a c/p"];
    const L_EV_FIN = ["V. Inversiones financieras a largo plazo",
                      "IV. Inversiones financieras a corto plazo"];
    const dInmov = _delta("Inmovilizado neto", a);
    const cfiRows = {
      inmovilizado: dInmov != null && amort != null ? -(dInmov + amort) : null,
      inv_inmobiliarias: 0,
      empresas_grupo: -_evDelta(L_EV_GRUPO),
      inv_financieras: -_evDelta(L_EV_FIN),
    };
    const cfiOtros = f.cfi != null ? f.cfi - _sum(cfiRows) : null;
    const cffRows = {
      patrimonio: dPN != null && neto != null ? dPN - neto : null,
      deuda: dDeuda,
      deuda_grupo: 0,
    };
    const cffOtros = f.cff != null ? f.cff - _sum(cffRows) : null;
    const _obj = (rows) => Object.fromEntries(
      Object.entries(rows).map(([k, v]) => [k, { valor: v }]));
    return {
      anio: a,
      proyectado: true,
      cfo: { ..._obj(cfoRows), otros: { valor: cfoOtros }, total: f.cfo ?? null },
      cfi: { ..._obj(cfiRows), otros: { valor: cfiOtros }, total: f.cfi ?? null },
      cff: { ..._obj(cffRows), otros: { valor: cffOtros }, total: f.cff ?? null },
      cf_total: f.delta_tesoreria ?? null,
      conciliacion: {
        saldo_inicial:
          f.tesoreria_final != null && f.delta_tesoreria != null
            ? f.tesoreria_final - f.delta_tesoreria
            : null,
        saldo_final_calculado: f.tesoreria_final ?? null,
        tesoreria_balance: f.tesoreria_final ?? null,
        diferencia: 0,
        check_ok: true,
      },
    };
  });
  // Dedupe (Héctor 13-oct): un año que es ejercicio real Y proyectado (año
  // base anterior) saldría dos veces — GANA EL EJERCICIO REAL: su EFE sale
  // de datos importados con desglose completo; la síntesis proyectada solo
  // tiene totales y nunca debe taparlo (la primera versión hacía ganar a la
  // proyectada y aparecieron MÁS N/D). La proyección de ese año se ve
  // desmarcándolo en "Histórico:".
  const realSelSet = new Set(flujosCaja.map((f) => f.anio));
  const flujosAll = [
    ...flujosCaja,
    ...flujosProy.filter((f) => !realSelSet.has(f.anio)),
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="comparativa-flujos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/empresas/${empresaId}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="page-title">
              Estado de Flujos de Caja
            </h1>
            <p className="text-muted-foreground text-sm">{empresa?.nombre}</p>
          </div>
        </div>
      </div>

      {/* Selector de Años */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle>Seleccionar Ejercicios</CardTitle>
          <CardDescription>El primer año ({primerAnio}) no está disponible (requiere datos del año anterior)</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-3">
            {ejercicios.map((ej) => {
              const isPrimerAnio = ej.anio === primerAnio;
              return (
                <div key={ej.anio} className="flex items-center space-x-2">
                  <Checkbox
                    id={`anio-${ej.anio}`}
                    checked={selectedAnios.includes(ej.anio)}
                    onCheckedChange={() => toggleAnio(ej.anio)}
                    disabled={isPrimerAnio}
                  />
                  <Label
                    htmlFor={`anio-${ej.anio}`}
                    className={`font-mono cursor-pointer ${isPrimerAnio ? 'text-muted-foreground line-through' : ''}`}
                  >
                    {ej.anio}
                  </Label>
                </div>
              );
            })}
            {aniosProyTodos.length > 0 && (
              <>
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">Proyección:</span>
                {aniosProyTodos.map((a) => (
                  <div key={a} className="flex items-center space-x-2">
                    <Checkbox
                      id={`anio-proy-${a}`}
                      checked={selectedProyAnios.includes(a)}
                      onCheckedChange={() =>
                        setSelectedProyAnios((prev) =>
                          prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a].sort()
                        )
                      }
                      data-testid={`select-anio-proy-${a}`}
                    />
                    <Label htmlFor={`anio-proy-${a}`} className="font-mono cursor-pointer text-primary flex items-center gap-1">
                      {a}
                      <span className="text-[10px] font-semibold uppercase rounded bg-proy-strong text-proy px-1 py-0.5">Proy.</span>
                    </Label>
                  </div>
                ))}
              </>
            )}
          </div>
          {aniosProyTodos.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => { setSelectedAnios(ejercicios.slice(1).map((e) => e.anio)); setSelectedProyAnios([...aniosProyTodos]); }}>
                Todo
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => { setSelectedAnios(ejercicios.slice(1).map((e) => e.anio)); setSelectedProyAnios([]); }}>
                Solo histórico
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => { setSelectedAnios([]); setSelectedProyAnios([...aniosProyTodos]); }}>
                Solo proyección
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contenido */}
      {flujosAll.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ArrowRightLeft className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Selecciona al menos un año (histórico o proyectado) para ver el Estado de Flujos de Caja</p>
          </CardContent>
        </Card>
      ) : loadingFlujos ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Tabla comparativa de CFO */}
          <ComparativaSection
            title="A) Cash Flow Operativo (CFO)"
            icon={TrendingUp}
            color="blue"
            expanded={expandedSections.cfo}
            onToggle={() => toggleSection("cfo")}
            flujos={flujosAll}
            items={[
              { key: 'cfo.resultado', label: 'Resultado del ejercicio' },
              { key: 'cfo.amortizacion', label: '(+) Amortización (sin caja)' },
              { key: 'cfo.impuesto_diferido', label: '(±) Impuesto diferido (474/479)' },
              { key: 'cfo.existencias', label: 'Existencias' },
              { key: 'cfo.clientes', label: 'Clientes' },
              { key: 'cfo.proveedores', label: 'Proveedores' },
              { key: 'cfo.periodificaciones_activo', label: 'Periodificaciones de activo' },
              { key: 'cfo.periodificaciones_pasivo', label: 'Periodificaciones de pasivo' },
              { key: 'cfo.provisiones', label: 'Provisiones' },
              { key: 'cfo.otros', label: 'Otros del modelo (proy.: intereses capitalizados…)' }
            ]}
            totalKey="cfo.total"
            onItemClick={handleItemClick}
          />

          {/* Tabla comparativa de CFI */}
          <ComparativaSection
            title="B) Cash Flow Inversión (CFI)"
            icon={Building2}
            color="amber"
            expanded={expandedSections.cfi}
            onToggle={() => toggleSection("cfi")}
            flujos={flujosAll}
            items={[
              { key: 'cfi.inmovilizado', label: 'Inmovilizado' },
              { key: 'cfi.inv_inmobiliarias', label: 'Inversiones inmobiliarias' },
              { key: 'cfi.empresas_grupo', label: 'Empresas del grupo' },
              { key: 'cfi.inv_financieras', label: 'Inversiones financieras' },
              { key: 'cfi.otros', label: 'Otros del modelo (proy.: resto de líneas de inversión)' }
            ]}
            totalKey="cfi.total"
            onItemClick={handleItemClick}
          />

          {/* Tabla comparativa de CFF */}
          <ComparativaSection
            title="C) Cash Flow Financiero (CFF)"
            icon={Wallet}
            color="purple"
            expanded={expandedSections.cff}
            onToggle={() => toggleSection("cff")}
            flujos={flujosAll}
            items={[
              { key: 'cff.patrimonio', label: 'Patrimonio neto (ampliaciones/dividendos/subvenciones)' },
              { key: 'cff.deuda', label: 'Deuda' },
              { key: 'cff.deuda_grupo', label: 'Deudas con empresas del grupo' },
              { key: 'cff.otros', label: 'Otros del modelo (proy.: eventos de balance…)' }
            ]}
            totalKey="cff.total"
            onItemClick={handleItemClick}
          />

          {/* Cash Flow Total */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-center gap-3 mb-4">
                <ArrowRightLeft className="h-5 w-5 text-primary" />
                <span className="font-semibold text-lg">D) CASH FLOW TOTAL</span>
              </div>
              <div className="overflow-x-auto">
                {/* Ancho MÍNIMO real por columna (Raúl 30-09: números superpuestos
                    con 6 años): table-fixed reparte lo que haya — sin mínimo,
                    con muchas columnas los importes se pisaban en vez de
                    activar el scroll horizontal. */}
                <table className={TABLA_ANIOS}>
                  <ColsAnios n={flujosAll.length} concepto={280} anio={COL_ANIO_MONO} />
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium text-muted-foreground w-[280px]">Concepto</th>
                      {flujosAll.map(f => (
                        <th key={f.anio} className={`text-right py-2 px-4 font-mono font-medium whitespace-nowrap ${f.proyectado ? 'text-proy' : ''}`}>
                          {f.anio}
                          {f.proyectado && (
                            <span className="ml-1.5 text-[10px] font-semibold uppercase rounded bg-proy-strong text-proy px-1 py-0.5 align-middle">Proy.</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="font-bold">
                      <td className="py-2 pr-4">Cash Flow Total</td>
                      {flujosAll.map(f => (
                        <td key={f.anio} className={`text-right py-2 px-4 font-mono ${f.proyectado ? 'bg-proy ' : ''}${(f.cf_total ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {f.error ? 'N/D' : formatCurrency(f.cf_total)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Conciliación */}
          <Card>
            <Collapsible open={expandedSections.conciliacion} onOpenChange={() => toggleSection("conciliacion")}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg text-emerald-600 bg-emerald-50 dark:bg-emerald-950">
                        <CheckCircle2 className="h-4 w-4" />
                      </div>
                      <CardTitle className="font-semibold">E) Conciliación (Check)</CardTitle>
                      {expandedSections.conciliacion ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="overflow-x-auto">
                    <table className={TABLA_ANIOS}>
                      <ColsAnios n={flujosAll.length} concepto={280} anio={COL_ANIO_MONO} />
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 font-medium text-muted-foreground w-[280px]">Concepto</th>
                          {flujosAll.map(f => (
                            <th key={f.anio} className={`text-right py-2 px-4 font-mono font-medium whitespace-nowrap ${f.proyectado ? 'text-proy' : ''}`}>
                              {f.anio}
                              {f.proyectado && (
                                <span className="ml-1.5 text-[10px] font-semibold uppercase rounded bg-proy-strong text-proy px-1 py-0.5 align-middle">Proy.</span>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="py-2 pr-4 text-muted-foreground">Tesorería inicial</td>
                          {flujosAll.map(f => (
                            <td key={f.anio} className={`text-right py-2 px-4 font-mono ${f.proyectado ? 'bg-proy' : ''}`}>
                              {f.error ? 'N/D' : formatCurrency(f.conciliacion?.saldo_inicial)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 text-muted-foreground">+ Cash Flow Total</td>
                          {flujosAll.map(f => (
                            <td key={f.anio} className={`text-right py-2 px-4 font-mono ${f.proyectado ? 'bg-proy ' : ''}${(f.cf_total ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {f.error ? 'N/D' : formatCurrency(f.cf_total)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t">
                          <td className="py-2 pr-4 font-medium">= Tesorería final calculada</td>
                          {flujosAll.map(f => (
                            <td key={f.anio} className={`text-right py-2 px-4 font-mono font-medium ${f.proyectado ? 'bg-proy' : ''}`}>
                              {f.error ? 'N/D' : formatCurrency(f.conciliacion?.saldo_final_calculado)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-medium">Tesorería según Balance</td>
                          {flujosAll.map(f => (
                            <td key={f.anio} className={`text-right py-2 px-4 font-mono font-medium ${f.proyectado ? 'bg-proy' : ''}`}>
                              {f.error ? 'N/D' : formatCurrency(f.conciliacion?.tesoreria_balance)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t bg-muted/30">
                          <td className="py-2 pr-4 font-semibold">Diferencia</td>
                          {flujosAll.map(f => (
                            <td key={f.anio} className={`text-right py-2 px-4 font-mono font-bold ${f.proyectado ? 'bg-proy ' : ''}${f.conciliacion?.check_ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {f.error ? 'N/D' : formatCurrency(f.conciliacion?.diferencia)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-semibold">Estado</td>
                          {flujosAll.map(f => (
                            <td key={f.anio} className="text-right py-2 px-4">
                              {f.error ? (
                                <Badge variant="outline" className="text-muted-foreground">N/D</Badge>
                              ) : f.conciliacion?.check_ok ? (
                                <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> OK
                                </Badge>
                              ) : (
                                <div className="flex flex-col items-end gap-1">
                                  <Badge variant="outline" className="text-rose-600 border-rose-600">
                                    <AlertCircle className="h-3 w-3 mr-1" /> Error
                                  </Badge>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 underline underline-offset-2"
                                    onClick={() => navigate(`/ejercicios/${f.ejercicio_id}/analisis?tab=flujos`)}
                                    data-testid={`como-arreglarlo-${f.anio}`}
                                  >
                                    <Wrench className="h-3 w-3" /> Cómo arreglarlo
                                  </button>
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </div>
      )}

      {/* Modal de Drill-down */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              {selectedItem?.label} ({selectedItem?.anio})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Valor</p>
              <p className={`text-2xl font-mono font-bold ${selectedItem?.valor >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {formatCurrency(selectedItem?.valor)}
              </p>
            </div>

            {selectedItem?.formula && (
              <div>
                <p className="text-sm font-medium mb-2">Fórmula</p>
                <code className="block p-3 bg-muted rounded text-sm">
                  {selectedItem.formula}
                </code>
              </div>
            )}

            {selectedItem?.explicacion && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 {selectedItem.explicacion}
                </p>
              </div>
            )}

            {selectedItem?.detalle && (
              <div>
                <p className="text-sm font-medium mb-2">Datos utilizados</p>
                <div className="border rounded-lg divide-y">
                  {Object.entries(selectedItem.detalle).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center px-3 py-2 text-sm">
                      <span className="text-muted-foreground">{formatDetailKey(key)}</span>
                      <span className="font-mono">{formatCurrency(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedItem?.subcuentas?.length > 0 && (
              <SubcuentasDesglose
                subcuentas={selectedItem.subcuentas}
                anio={selectedItem?.anio}
                anioAnterior={selectedItem?.anio ? selectedItem.anio - 1 : null}
                onExport={selectedItem?.anio ? () => handleExportarVariaciones(selectedItem.anio) : undefined}
                exportando={exportandoVariaciones}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubcuentasDesglose({ subcuentas, anio, anioAnterior, onExport, exportando }) {
  const totalDelta = subcuentas.reduce((acc, c) => acc + (c.delta || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium">Desglose por subcuenta</p>
        {onExport && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 text-xs"
            onClick={onExport}
            disabled={exportando}
            data-testid="exportar-variaciones-modal"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            {exportando ? "Exportando..." : "Exportar Excel"}
          </Button>
        )}
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted text-xs text-muted-foreground border-b">
              <th className="text-left px-3 py-2 font-semibold">Código</th>
              <th className="text-left px-3 py-2 font-semibold">Nombre</th>
              <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Saldo {anioAnterior ?? "t-1"}</th>
              <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Saldo {anio ?? "t"}</th>
              <th className="text-right px-3 py-2 font-semibold">Δ</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {subcuentas.map((c) => (
              <tr key={c.codigo} className="hover:bg-muted/40">
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap align-top">{c.codigo}</td>
                <td className="px-3 py-2 align-top">{c.nombre}</td>
                <td className="text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap align-top">{formatCurrency(c.saldo_t1)}</td>
                <td className="text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap align-top">{formatCurrency(c.saldo_t)}</td>
                <td className={`text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap align-top font-medium ${c.delta > 0 ? 'text-emerald-600' : c.delta < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                  {c.delta > 0 ? '+' : ''}{formatCurrency(c.delta)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t bg-muted/50 font-semibold">
              <td className="px-3 py-2" colSpan={4}>Variación total</td>
              <td className={`text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap ${totalDelta > 0 ? 'text-emerald-600' : totalDelta < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                {totalDelta > 0 ? '+' : ''}{formatCurrency(totalDelta)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function ComparativaSection({ title, icon: Icon, color, expanded, onToggle, flujos, items, totalKey, onItemClick }) {
  const colorClasses = {
    blue: "text-blue-600 bg-blue-50 dark:bg-blue-950",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-950",
    purple: "text-purple-600 bg-purple-50 dark:bg-purple-950"
  };

  const getValue = (flujo, key) => {
    if (flujo.error) return null;
    const parts = key.split('.');
    let value = flujo;
    for (const part of parts) {
      if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return null;
      }
    }
    // Clave ausente (p.ej. `otros` no existe en el EFE real) → null, no
    // undefined: si no, las filas "Otros del modelo" salían "N/D" en todas
    // las columnas reales en vez de '—'.
    const v = value !== null && typeof value === 'object' ? value.valor : value;
    return v === undefined ? null : v;
  };

  return (
    <Card>
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <CardTitle className="font-semibold">{title}</CardTitle>
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className={TABLA_ANIOS}>
                <ColsAnios n={flujos.length} concepto={280} anio={COL_ANIO_MONO} />
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground w-[280px]">Concepto</th>
                    {flujos.map(f => (
                      <th key={f.anio} className={`text-right py-2 px-4 font-mono font-medium whitespace-nowrap ${f.proyectado ? 'text-proy' : ''}`}>
                        {f.anio}
                        {f.proyectado && (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase rounded bg-proy-strong text-proy px-1 py-0.5 align-middle">Proy.</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.key} className="hover:bg-muted/30">
                      {/* El concepto (y su ℹ️) también abre el detalle — del
                          último año HISTÓRICO (el drill-down por subcuenta no
                          existe en proyección) — para que el icono no invite a
                          un click muerto. Las celdas de importe siguen
                          abriendo su año. */}
                      <td
                        className="py-2 pr-4 text-sm cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          const historicos = flujos.filter(f => !f.proyectado);
                          if (historicos.length > 0) {
                            onItemClick(item.key, historicos[historicos.length - 1].anio);
                          }
                        }}
                      >
                        <div className="flex items-center gap-1">
                          {item.label}
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </td>
                      {flujos.map(f => {
                        // E6 (oct-2026): el desglose proyectado se DERIVA del
                        // consolidado (Δ de las mismas series del balance);
                        // "—" solo cuando de verdad no hay dato.
                        if (f.proyectado) {
                          const vp = getValue(f, item.key);
                          return (
                            <td key={f.anio} className={`text-right py-2 px-4 font-mono text-sm bg-proy ${
                              vp == null ? 'text-muted-foreground' :
                              vp > 0 ? 'text-emerald-600' : vp < 0 ? 'text-rose-600' : ''
                            }`}>
                              {vp == null ? '—' : formatCurrency(vp)}
                            </td>
                          );
                        }
                        const value = getValue(f, item.key);
                        return (
                          <td
                            key={f.anio}
                            className={`text-right py-2 px-4 font-mono text-sm cursor-pointer hover:bg-muted/50 ${
                              value === null ? 'text-muted-foreground' :
                              value > 0 ? 'text-emerald-600' :
                              value < 0 ? 'text-rose-600' : ''
                            }`}
                            onClick={() => onItemClick(item.key, f.anio)}
                          >
                            {value === null
                              ? (item.key.endsWith('.otros') ? '—' : 'N/D')
                              : formatCurrency(value)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="border-t font-bold bg-muted/30">
                    <td className="py-2 pr-4">Total</td>
                    {flujos.map(f => {
                      const value = getValue(f, totalKey);
                      return (
                        <td key={f.anio} className={`text-right py-2 px-4 font-mono ${f.proyectado ? 'bg-proy ' : ''}${
                          value == null ? 'text-muted-foreground' :
                          value >= 0 ? 'text-emerald-600' : 'text-rose-600'
                        }`}>
                          {value == null ? 'N/D' : formatCurrency(value)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function formatDetailKey(key) {
  return key
    .replace(/_t1$/, ' (año t-1)')
    .replace(/_t$/, ' (año t)')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}
